import React from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '@mui/system'

type LKParams = {
  link: string
  alt: string
  onBeforeNavigate?: (params?: { [key: string]: any }) => void | Promise<void>
  classNameObj?: React.HTMLAttributes<HTMLDivElement> | string
  target?: string
  rel?: string
  role?: string
  awaitBeforeNavigate?: boolean
}

const DefaultLinkStyle = styled('div')(({ theme }) => ({
  '& .default': {
    cursor: 'pointer',
    '& a': {
      textDecoration: 'none',
      cursor: 'pointer',
      color: 'inherit',
      display: 'contents',
    },
    '& div[role="link"]': {
      cursor: 'pointer',
      textDecoration: 'none',
      color: 'inherit',
      display: 'contents',
    },
  },
}))

export const Lk: React.FC<React.PropsWithChildren<LKParams>> = ({
  link,
  classNameObj,
  onBeforeNavigate,
  children,
  alt,
  target,
  rel,
  role = 'link',
  awaitBeforeNavigate = false,
}) => {
  const navigate = useNavigate()

  const handleExternalClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (onBeforeNavigate) {
      if (awaitBeforeNavigate) {
        await onBeforeNavigate()
      } else {
        onBeforeNavigate()
      }
    }
    if (typeof window !== 'undefined') {
      window.location.href = link
    }
  }

  const handleInternalClick = async (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (onBeforeNavigate) {
      if (awaitBeforeNavigate) {
        await onBeforeNavigate()
      } else {
        onBeforeNavigate()
      }
    }
    navigate(link)
  }

  return (
    <DefaultLinkStyle className={classNameObj ? `${classNameObj}` : 'default'}>
      {link && link?.includes('http') && (
        <a
          href={link}
          aria-label={alt}
          target={target}
          rel={rel || 'noopener noreferrer'}
          role={role}
          onClick={handleExternalClick}
        >
          {children}
        </a>
      )}
      {link && !link?.includes('http') && (
        <div
          onClick={handleInternalClick}
          role={role}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleInternalClick(e)
            }
          }}
          aria-label={alt}
        >
          {children}
        </div>
      )}
    </DefaultLinkStyle>
  )
}
